// ─── SCENE-RANKED RETRIEVALS, HELD STABLE FOR A WINDOW ──────────────────────
//
// Voice samples, the story-arc bundle, linked memory and the archive are all
// ranked by a scene embedding, so they changed on EVERY turn. They sit in the
// system message (shape A2 keeps memory up front — the tail layout shortened
// replies by two thirds on 2026-09-08), and a prompt cache is a prefix match:
// the first changed byte ends it. So the cacheable prefix ended where these
// sections began and the whole transcript behind them was re-billed every
// turn — 24–35 % cached in production, measured 2026-10-05, on a host that
// reuses 100 % of an unchanged prefix (probe, 2026-10-06).
//
// Holding these sections byte-identical for a window keeps memory up front
// AND keeps the system message + transcript a stable prefix across turns.
// The window is time-based (15 minutes of play) and keyed on the things that
// genuinely change what should be retrieved (scene, responder, cast). A
// regeneration inside the window reuses the window — it is the same scene.
// The cost is relevance drift of at most one window; the recent-events and
// summary sections are always current regardless.
export const EPOCH_MS = 15 * 60 * 1000;

const cache = new Map(); // scopeId -> { key, value }

/** The window key for a scope: scope, 15-minute bucket, and the inputs that must match. */
export function epochKey(scopeId, parts = [], now = Date.now()) {
  const bucket = Math.floor(now / EPOCH_MS);
  return `${scopeId}|${bucket}|${(parts || []).map(p => (p == null ? '' : String(p))).join('|')}`;
}

/** Run `builder` once per key for the scope; later calls with the same key get the same value. */
export async function withEpochCache(scopeId, key, builder) {
  const hit = cache.get(scopeId);
  if (hit && hit.key === key) return hit.value;
  const value = await builder();
  cache.set(scopeId, { key, value });
  return value;
}

/** Drop one scope (or everything) — e.g. after the writer edits lore. */
export function clearEpochCache(scopeId = null) {
  if (scopeId) cache.delete(scopeId); else cache.clear();
}
